import Image from "next/image";
import React, {ImgHTMLAttributes, useMemo} from "react";
import {cn, rgbDataURL} from "@/lib/utils";
import {bowlbyOne, poppins} from "@/app/fonts/fonts";
import styles from './game-card.module.css';
import {InfoCircleFilledSvg} from "@/app/components/icons/InfoCircleFilledSvg";
import {useTranslations} from "next-intl";

interface IProps extends ImgHTMLAttributes<HTMLImageElement> {
    poster?: string;
    gameName: string;
    providerName: string;
    size?: 'sm',
    isUnavailable?: boolean;
}

const getFallbackPoster = (name: string) => {
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `/game-${(hash % 8) + 1}.png`;
}

export default function GameCard({
                                     className,
                                     sizes,
                                     gameName,
                                     providerName,
                                     poster,
                                     size,
                                     isUnavailable,
                                     ...props
                                 }: IProps) {
    const t = useTranslations();
    const memoPoster = useMemo(() => {
        return poster || getFallbackPoster(gameName)
    }, [poster, gameName])

    const gameChunks = useMemo(() => {
        return gameName.split(" ").length;
    }, [gameName])

    return (
        <div
            className={cn('relative aspect-3/4 rounded-[16px]', {['transition duration-300 ease-in-out hover:-translate-y-3']: !isUnavailable}, className)}  {...props}>
            <Image
                className={'rounded-[8px]'}
                sizes={sizes}
                fill
                style={{
                    objectFit: 'contain',
                }}
                blurDataURL={rgbDataURL(32, 36, 40)}
                placeholder={'blur'}
                src={memoPoster}
                alt={gameName}
            />

            {
                !poster ? (
                    <>
                        <div
                            className={cn(`${bowlbyOne.className} absolute uppercase flex justify-center items-center text-center text-white text-shadow-lg left-[16px] right-[16px]`, {
                                [styles.Title]: !size,
                                [styles.TitleSm]: size === 'sm',
                                ['break-all']: gameChunks === 1
                            })}>{gameName}</div>
                        <div
                            className={cn(`${poppins.className} absolute uppercase text-center text-white left-0 right-0`, {
                                [styles.Provider]: !size,
                                [styles.ProviderSm]: size === 'sm',
                            })}>{providerName}</div>
                    </>
                ) : null
            }

            {isUnavailable ? (
                <div
                    className="group absolute inset-0 bg-dark-gray/80 text-white/60 flex items-end p-[8px] hover:p-[15px]">
                    <div
                        className={'opacity-0 w-0 group-hover:opacity-100 group-active:opacity-100 group-hover:w-full group-active:w-full flex flex-col items-center justify-center'}>
                        <InfoCircleFilledSvg/>

                        <span className="font-bold text-[13px] md:text-[18px] leading-none text-center">
                            {t('GameCard.unavailableInYourRegion')}
                        </span>
                    </div>
                    <div className={'group-hover:opacity-0 group-active:opacity-0 group-hover:w-0 group-active:w-0'}>
                        <InfoCircleFilledSvg/>
                    </div>
                </div>

            ) : null}
        </div>
    )
}
